import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSetUnavailableMutation } from '../../Api/unavailability';
import useTranslate from '../../Hooks/useTranslate';
import Button from '../../Style-guide/Button';
import Input from '../../Style-guide/Input';
import Modal from '../../Style-guide/Modal';
import notify from '../../Style-guide/ToastMessage';

const SetUnavailableModal = (props) => {
  const { isModalOpen, setIsModalOpen, modalInputs, setModalInputs, courtId } = props;
  const queryClient = useQueryClient();
  const tr = useTranslate();

  const {
    mutate: sendUnavailableRequest,
    data: response,
    isSuccess,
  } = useSetUnavailableMutation();

  useEffect(() => {
    if (isSuccess && response) {
      queryClient.invalidateQueries({
        queryKey: ['unavailable-periods'],
      });
      setIsModalOpen(false);
      setModalInputs({ dateFrom: null, dateUntil: null, timeFrom: '', timeUntil: '' });
    }
  }, [isSuccess, queryClient, response, setIsModalOpen, setModalInputs]);

  const handleChange = (key, value) => {
    setModalInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateInputs = () => {
    const missingValue = Object.values(modalInputs).filter((item) => !item);
    if (missingValue.length) {
      return {
        isValid: false,
        message: 'Missing values',
        translationKey: 'missing_required_values',
        value: {},
      };
    }

    const buildGMT4Date = (dateObj, time) => {
      const y = dateObj.getFullYear();
      const m = dateObj.getMonth();
      const d = dateObj.getDate();

      const [hh, mm] = time.split(':').map(Number);

      // interpret as GMT+4 → convert to UTC
      return new Date(Date.UTC(y, m, d, hh - 4, mm));
    };

    const from = buildGMT4Date(modalInputs.dateFrom, modalInputs.timeFrom);
    const until = buildGMT4Date(modalInputs.dateUntil, modalInputs.timeUntil);

    const isValidFrom = !Number.isNaN(from.getTime());
    const isValidUntil = !Number.isNaN(until.getTime());

    if (!isValidFrom || !isValidUntil) {
      return {
        isValid: false,
        message: 'Invalid values',
        translationKey: 'invalid_values',
        value: {},
      };
    }

    if (from < new Date()) {
      return {
        isValid: false,
        message: 'Date must be in the future',
        translationKey: 'date_must_be_in_the_future',
        value: {},
      };
    }

    if (from >= until) {
      return {
        isValid: false,
        message: 'End date must be later than start date',
        translationKey: 'end_date_must_be_later_than_start_date',
        value: {},
      };
    }

    return { isValid: true, message: '', translationKey: '', value: { from, until } };
  };

  const handleSubmit = () => {
    const { isValid, message, value, translationKey } = validateInputs();
    if (!isValid) {
      notify.error(tr(translationKey, { isError: true }) || message);
    } else {
      sendUnavailableRequest({ data: value, params: { courtId } });
    }
  };

  return (
    <Modal
      show={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      className='set-unavailable-modal'
    >
      <div className='inputs-container date-inputs'>
        <Input
          type='date'
          className='disable-modal-input'
          name='disableDateFrom'
          value={modalInputs.dateFrom}
          onChange={(date) => handleChange('dateFrom', date)}
        />
        <span className='hyphen' />
        <Input
          type='date'
          className='disable-modal-input'
          name='disableDateUntil'
          value={modalInputs.dateUntil}
          onChange={(date) => handleChange('dateUntil', date)}
        />
      </div>

      <div className='inputs-container time-inputs'>
        <Input
          type='time'
          className='disable-modal-input'
          name='disableTimeFrom'
          value={modalInputs.timeFrom}
          step={600}
          onChange={(e) => handleChange('timeFrom', e.target.value)}
        />
        <span className='hyphen' />
        <Input
          type='time'
          className='disable-modal-input'
          name='disableTimeUntil'
          step={600}
          value={modalInputs.timeUntil}
          onChange={(e) => handleChange('timeUntil', e.target.value)}
        />
      </div>
      <Button className='modal-submit-btn' onClick={handleSubmit}>
        {tr('submit')}
      </Button>
    </Modal>
  );
};

export default SetUnavailableModal;
